import { Bell, CheckCircle, ExternalLink } from 'lucide-react';
import { clsx } from 'clsx';
import type { CentralNotification } from '@levelup/central-notifications';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { useWorkspaceNotifications } from '../hooks/useWorkspaceNotifications';

/**
 * Alerts Page — Central-backed.
 *
 * Workspace alerts are sourced exclusively from @levelup/central-notifications.
 * This app no longer maintains a local /alerts backend. Mark-read is delegated
 * to Central; Central's notification category/severity drives presentation.
 */
export function AlertsPage() {
  const { notifications, isLoading, isError, markRead } = useWorkspaceNotifications();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const active = notifications.filter((n) => !n.isRead);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Alerts</h1>
        <p className="text-gray-600">
          Review and respond to important changes, deadlines, and compliance events
        </p>
      </div>

      {isError ? (
        <div className="card">
          <EmptyState
            icon={Bell}
            title="Unable to load alerts"
            description="We couldn't reach Central to fetch your workspace alerts. Try again in a moment."
          />
        </div>
      ) : active.length > 0 ? (
        <div className="space-y-4">
          {active.map((n) => (
            <AlertCard key={n.id} notification={n} onMarkRead={() => markRead(n.id)} />
          ))}
        </div>
      ) : (
        <div className="card">
          <EmptyState
            icon={Bell}
            title="All clear"
            description="No active alerts right now. Alerts will appear here when Central detects changes that need your attention — like missed deadlines, compliance issues, or account drift."
          />
        </div>
      )}
    </div>
  );
}

function AlertCard({
  notification,
  onMarkRead,
}: {
  notification: CentralNotification;
  onMarkRead: () => void;
}) {
  const severity = inferSeverity(notification);
  return (
    <div
      className={clsx(
        'card border-l-4',
        severity === 'critical' && 'border-l-red-500',
        severity === 'high' && 'border-l-orange-500',
        severity === 'medium' && 'border-l-yellow-500',
        severity === 'low' && 'border-l-blue-500',
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <SeverityBadge severity={severity} />
            {notification.appMetadata?.appDisplayName && (
              <span className="text-xs text-gray-500">
                {notification.appMetadata.appDisplayName}
              </span>
            )}
          </div>
          <h3 className="font-semibold text-gray-900">{notification.title}</h3>
          {notification.body && (
            <p className="text-gray-600 mt-1">{notification.body}</p>
          )}
          <p className="text-xs text-gray-500 mt-2">
            {new Date(notification.createdAt).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="flex items-center space-x-2 mt-4 pt-4 border-t">
        <button
          onClick={onMarkRead}
          className="flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-legacy-600 text-white hover:bg-legacy-700 transition-colors"
        >
          <CheckCircle className="h-4 w-4 mr-1.5" />
          Mark as read
        </button>
        {notification.actionUrl && (
          <a
            href={notification.actionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <ExternalLink className="h-4 w-4 mr-1.5" />
            Open
          </a>
        )}
      </div>
    </div>
  );
}

function inferSeverity(n: CentralNotification): 'critical' | 'high' | 'medium' | 'low' {
  // Central may carry priority/severity in metadata; fall back to the type
  // string which sometimes encodes severity (e.g. "billing.critical").
  const priority = (n.metadata as Record<string, unknown> | null)?.priority;
  if (priority === 'critical' || priority === 'high' || priority === 'medium' || priority === 'low') {
    return priority;
  }
  const type = (n.type ?? '').toLowerCase();
  if (type.includes('critical') || type.includes('security')) return 'critical';
  if (type.includes('warning') || type.includes('deadline')) return 'high';
  if (type.includes('info')) return 'low';
  return 'medium';
}

function SeverityBadge({ severity }: { severity: 'critical' | 'high' | 'medium' | 'low' }) {
  const colors: Record<string, string> = {
    critical: 'bg-red-100 text-red-800',
    high: 'bg-orange-100 text-orange-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-blue-100 text-blue-800',
  };
  return (
    <span className={clsx('px-2 py-1 text-xs font-medium rounded-full', colors[severity])}>
      {severity}
    </span>
  );
}
